import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, AuthState, LoginRequest, SignupRequest } from '../types';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';

interface AuthStore extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  signup: (userData: SignupRequest) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  initializeAuth: () => void;
  setUser: (user: User) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => {
      // Listen for auth logout events from API interceptor
      if (typeof window !== 'undefined') {
        window.addEventListener('auth:logout', () => {
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        });
        window.addEventListener('auth:tokens-refreshed', ((e: CustomEvent<{ access_token: string; refresh_token: string | null }>) => {
          const { access_token, refresh_token } = e.detail ?? {};
          if (access_token) set({ token: access_token, refreshToken: refresh_token ?? null });
        }) as EventListener);
      }
      
      return {
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true });
        try {
          const tokenResponse = await apiService.login(credentials);
          const accessToken = tokenResponse.access_token;
          const refreshToken = tokenResponse.refresh_token ?? null;

          // Store both tokens in API service and localStorage (for 401 refresh flow)
          apiService.setAuthTokens(accessToken, refreshToken);

          // Fetch full user profile from API
          const user = await apiService.getCurrentUser();

          localStorage.setItem('user', JSON.stringify(user));

          set({
            user,
            token: accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          set({ isLoading: false });
          const message = error.response?.data?.message || 'Login failed';
          toast.error(message);
          throw error;
        }
      },

      signup: async (userData: SignupRequest) => {
        set({ isLoading: true });
        try {
          const user = await apiService.signup(userData);
          
          // After successful signup, automatically log in
          await get().login({
            email: userData.email,
            password: userData.password,
          });
        } catch (error: any) {
          set({ isLoading: false });
          const message = error.response?.data?.message || 'Signup failed';
          toast.error(message);
          throw error;
        }
      },

      logout: () => {
        apiService.removeAuthToken();
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
        toast.success('Logged out successfully');
      },

      clearError: () => {
        // This would clear any error state if we had one
      },

      initializeAuth: async () => {
        // Sync from persisted state to localStorage so api interceptor has tokens (e.g. after reload)
        const stateToken = get().token;
        const stateRefresh = get().refreshToken;
        if (stateToken && !apiService.getAuthToken()) {
          apiService.setAuthTokens(stateToken, stateRefresh ?? null);
        }

        const token = apiService.getAuthToken();
        const savedUser = localStorage.getItem('user');

        if (token && savedUser) {
          try {
            // Try to fetch fresh user data from API
            try {
              const user = await apiService.getCurrentUser();
              localStorage.setItem('user', JSON.stringify(user));
              set({
                user,
                token,
                isAuthenticated: true,
                isLoading: false,
              });
            } catch (error: any) {
              // If API call fails with 401, user doesn't exist or token is invalid
              if (error.response?.status === 401) {
                apiService.removeAuthToken();
                set({
                  user: null,
                  token: null,
                  refreshToken: null,
                  isAuthenticated: false,
                  isLoading: false,
                });
              } else {
                // For other errors, try using saved user data as fallback
                try {
                  const user = JSON.parse(savedUser);
                  set({
                    user,
                    token,
                    isAuthenticated: true,
                    isLoading: false,
                  });
                } catch {
                  apiService.removeAuthToken();
                  set({
                    user: null,
                    token: null,
                    refreshToken: null,
                    isAuthenticated: false,
                    isLoading: false,
                  });
                }
              }
            }
          } catch {
            apiService.removeAuthToken();
            set({
              user: null,
              token: null,
              refreshToken: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } else {
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setUser: (user: User) => {
        localStorage.setItem('user', JSON.stringify(user));
        set({ user });
      },
      };
    },
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
